import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getOwnerDashboard } from "../lib/ownerPortal";
import {
  ChevronRight,
  ArrowUpRight,
  Compass,
  MapPin,
  MessageSquare,
  CreditCard,
  AlertCircle,
  Bookmark,
  Activity as ActivityIcon,
  Hammer,
} from "lucide-react";
import { timeOfDayGreeting, relativeDate, WaitingOnPill, stageBlocker } from "../lib/ownerUi";
import { assetUrl, formatPriceCompact } from "../lib/format";

/* ──────────────── Stage progress helpers ──────────────── */

const STAGE_ORDER = [
  "Selection", "Consultation", "Purchase", "Expedited Review",
  "Build", "Construction Inspection",
];

function stageProgress(stage) {
  if (!stage) return 0;
  const idx = STAGE_ORDER.findIndex(
    (s) => s.toLowerCase() === String(stage).toLowerCase(),
  );
  if (idx < 0) return 0;
  return ((idx + 1) / STAGE_ORDER.length) * 100;
}

/* ──────────────── Activity humanizer ──────────────── */

const ACTION_LABEL = {
  profile_updated: "updated their profile",
  plans_purchased: "completed a plan purchase",
  document_generated: "generated a document",
  saved: "saved a design",
  unsaved: "unsaved a design",
  "inspection.logged": "logged an inspection",
  "activation.approved": "approved an activation",
  consultation_replied: "replied to a consultation",
  message_sent: "sent a message",
  "stage.transitioned": "advanced the project stage",
};

function actionLabel(a) {
  const action = a?.action || "did something";
  return ACTION_LABEL[action] || action.replace(/[_.]/g, " ");
}

/* ──────────────── Page ──────────────── */

export default function OwnerDashboard() {
  const { user } = useRole();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getOwnerDashboard(user.id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (err) {
    return (
      <section data-testid="owner-dashboard-error">
        <p style={{ color: "#A23B2E" }}>
          Could not load your workspace. Please try again shortly.
        </p>
      </section>
    );
  }
  if (!data) {
    return (
      <section data-testid="owner-dashboard-loading">
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading your workspace…
        </div>
      </section>
    );
  }

  const { user: u, stats, projects, conversations, saved_designs,
          activity, action_queue, finance, civic } = data;
  const greeting = timeOfDayGreeting();
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // Greeting name: prefer explicit first_name (contact person on an
  // organisation account), then a person's first token, then drop the
  // name altogether so org accounts don't see "Good morning, Near."
  const ORG_TOKENS = /(trust|cdc|llc|inc\.?|corp\.?|association|foundation|alliance|coalition|partnership|development corporation)/i;
  const rawName = (u?.name || "").trim();
  const isOrgName = rawName && (ORG_TOKENS.test(rawName) || (u?.organization && u.organization.toLowerCase().includes(rawName.toLowerCase())));
  let greetingName = "";
  if (u?.first_name && u.first_name.trim()) {
    greetingName = u.first_name.trim();
  } else if (rawName && !isOrgName) {
    greetingName = rawName.split(" ")[0];
  }

  return (
    <section className="animate-fade-in-up" data-testid="owner-workspace">
      {/* ─── § 01 · Greeting + dateline ─── */}
      <header
        className="grid lg:grid-cols-[1fr_auto] gap-10 items-end"
        style={{ paddingBottom: 0 }}
      >
        <div>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 28,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              Owner workspace
            </span>
          </div>
          <h1
            className="mt-4"
            data-testid="ws-greeting"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(34px, 4.5vw, 50px)",
              fontWeight: 500,
              lineHeight: 1.06,
              letterSpacing: "-0.01em",
              color: "#1B1F4B",
              margin: 0,
            }}
          >
            {greetingName ? `${greeting}, ${greetingName}.` : `${greeting}.`}
          </h1>
          <p
            className="mt-3"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 17,
              lineHeight: 1.55,
              maxWidth: 640,
              margin: 0,
            }}
          >
            {isOrgName ? (
              <>
                <span style={{ color: "#272636" }}>{u.organization || u.name}</span>
                {" · Owner-Builder · "}
                {civic?.neighborhood || "Indianapolis"}
              </>
            ) : (
              <>
                {u?.organization && (
                  <>
                    <span style={{ color: "#272636" }}>{u.organization}</span>
                    {" · "}
                  </>
                )}
                Owner-Builder · {civic?.neighborhood || "Indianapolis"}
              </>
            )}
          </p>
        </div>
        <aside
          className="hidden lg:block"
          style={{
            borderLeft: "1px solid #D9D8E4",
            paddingLeft: 20,
            minWidth: 200,
          }}
        >
          <div className="wn-label" style={{ color: "#6C6B85" }}>
            Today
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              color: "#1B1F4B",
              fontSize: 14.5,
              letterSpacing: "0.02em",
            }}
          >
            {today}
          </div>
          <div
            className="mt-2 tabular-nums"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {stats?.active_projects ?? 0} active
            {" · "}
            {stats?.open_consultations ?? 0} consultations
            {" · "}
            {stats?.saved_designs ?? 0} saved
          </div>
        </aside>
      </header>

      {/* ─── § 02 · Action queue ─── */}
      <ActionQueue items={action_queue || []} />

      {/* ─── § 03 · Active projects (supercharged) ─── */}
      <div style={{ marginTop: 56 }}>
      <SectionHeading
        marker="§ 03"
        eyebrow="In motion"
        title="Your projects."
        trailing={
          <Link
            to="/owner/projects"
            className="wn-cta-arrow inline-flex items-center"
            style={{
              color: "#8F3C23",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 13,
              gap: 6,
              textDecoration: "none",
            }}
            data-testid="ws-see-all-projects"
          >
            See all <ArrowUpRight size={13} />
          </Link>
        }
      />
      {projects && projects.length > 0 ? (
        <div
          className="mt-6 grid md:grid-cols-2 lg:grid-cols-3"
          style={{ gap: 20 }}
          data-testid="ws-projects"
        >
          {projects.map((p) => (
            <ProjectCard key={p.id} p={p} />
          ))}
        </div>
      ) : (
        <EmptyTile
          title="No active projects yet."
          body="Find a design that fits your parcel and request a consultation to get started."
          cta={{ label: "Browse the marketplace", to: "/marketplace" }}
        />
      )}
      </div>

      {/* ─── § 04 · Workspace shortcuts ─── */}
      <Shortcuts />

      {/* ─── § 05 · Financial snapshot ─── */}
      <FinanceSnapshot finance={finance} />

      {/* ─── § 07 · Civic context ─── */}
      <CivicContext civic={civic} />

      {/* ─── § 06 · Conversations + Saved ─── */}
      <div
        className="grid lg:grid-cols-2"
        style={{ gap: 40, marginTop: 56 }}
      >
        <ConversationsBlock items={conversations} />
        <SavedDesignsBlock items={saved_designs} />
      </div>

      {/* ─── § 08 · Activity feed ─── */}
      <ActivityFeed items={activity || []} projects={projects || []} />
    </section>
  );
}

/* ──────────────── § 02 · Action queue ──────────────── */

function ActionQueue({ items }) {
  return (
    <div data-testid="ws-action-queue" style={{ marginTop: 56 }}>
      <SectionHeading
        marker="§ 02"
        eyebrow="Needs your attention"
        title={items.length > 0 ? "Open items." : "All caught up."}
        trailing={
          items.length > 0 && (
            <span
              className="tabular-nums wn-label"
              data-testid="ws-action-count"
              style={{ color: "#8F3C23" }}
            >
              {items.length} item{items.length === 1 ? "" : "s"}
            </span>
          )
        }
      />
      {items.length === 0 ? (
        <div
          className="mt-6"
          style={{
            border: "1px solid #D9D8E4",
            borderRadius: 4,
            backgroundColor: "#FBFAFD",
            padding: "28px 28px 26px",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: 24,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: 22,
                color: "#1B1F4B",
                lineHeight: 1.2,
              }}
            >
              You&rsquo;re all caught up.
            </div>
            <div
              className="mt-2"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 15,
              }}
            >
              No replies, signatures, or payments pending. Want to start
              something new?
            </div>
          </div>
          <Link
            to="/marketplace"
            data-testid="ws-action-empty-cta"
            className="wn-btn wn-btn-accent inline-flex items-center"
            style={{ gap: 8 }}
          >
            Start something new
            <ArrowUpRight size={14} />
          </Link>
        </div>
      ) : (
        <div
          className="mt-6"
          style={{
            border: "1px solid #D9D8E4",
            borderRadius: 4,
            backgroundColor: "#FBFAFD",
            overflow: "hidden",
          }}
        >
          {items.map((a, i) => (
            <ActionRow key={i} item={a} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionRow({ item, index }) {
  const tone = item.priority === "high"
    ? { dot: "#B14E2F", label: "URGENT" }
    : { dot: "#A68A3F", label: "OPEN" };
  return (
    <Link
      to={item.cta_href || "#"}
      data-testid={`ws-action-${item.kind}`}
      className="grid items-center wn-action-row"
      style={{
        gridTemplateColumns: "auto 1fr auto",
        gap: 20,
        padding: "18px 24px",
        borderTop: index === 0 ? "none" : "1px solid #ECEBF1",
        textDecoration: "none",
        color: "inherit",
        transition: "background-color 160ms ease",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.backgroundColor = "#F5F4FA")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor = "transparent")
      }
    >
      <span
        aria-hidden
        style={{
          width: 10,
          height: 10,
          borderRadius: 999,
          background: tone.dot,
          flexShrink: 0,
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div className="flex items-center gap-2">
          <span
            className="wn-label"
            style={{ color: tone.dot, letterSpacing: "0.10em" }}
          >
            {tone.label}
          </span>
          <span aria-hidden style={{ color: "#D9D8E4" }}>
            |
          </span>
          <span className="wn-label" style={{ color: "#6C6B85" }}>
            {relativeDate(item.context_at) || "Now"}
          </span>
        </div>
        <div
          className="mt-1"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 18,
            color: "#1B1F4B",
            lineHeight: 1.25,
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            color: "#272636",
            fontSize: 13.5,
            marginTop: 2,
            lineHeight: 1.4,
          }}
        >
          {item.subtitle}
        </div>
      </div>
      <span
        className="wn-cta-arrow inline-flex items-center"
        style={{
          color: "#8F3C23",
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 700,
          fontSize: 13,
          gap: 6,
          whiteSpace: "nowrap",
        }}
      >
        {item.cta_label}
        <ArrowUpRight size={14} />
      </span>
    </Link>
  );
}

/* ──────────────── § 04 · Workspace shortcuts ──────────────── */

function Shortcuts() {
  const tiles = [
    { icon: Compass, label: "Browse marketplace",
      sub: "Find pre-reviewed designs",
      to: "/marketplace", testid: "sc-marketplace" },
    { icon: MapPin, label: "Find a parcel",
      sub: "Locate vacant lots",
      to: "/parcels", testid: "sc-parcels" },
    { icon: MessageSquare, label: "Request consultation",
      sub: "Talk to an architect",
      to: "/architects", testid: "sc-consultation" },
    { icon: CreditCard, label: "View payments",
      sub: "Receipts & history",
      to: "/owner/payments", testid: "sc-payments" },
  ];
  return (
    <div
      style={{ marginTop: 56 }}
      data-testid="ws-shortcuts"
    >
      <SectionHeading
        marker="§ 04"
        eyebrow="Quick launch"
        title="Workspace shortcuts."
      />
      <div
        className="mt-6 grid grid-cols-2 lg:grid-cols-4"
        style={{ gap: 12 }}
      >
        {tiles.map((t) => (
          <Link
            key={t.label}
            to={t.to}
            data-testid={t.testid}
            className="wn-shortcut-tile"
            style={{
              display: "block",
              padding: "20px 20px 18px",
              border: "1px solid #D9D8E4",
              borderRadius: 4,
              backgroundColor: "#FBFAFD",
              textDecoration: "none",
              color: "inherit",
              transition: "border-color 160ms ease, background-color 160ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#B14E2F";
              e.currentTarget.style.backgroundColor = "#F5F4FA";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#D9D8E4";
              e.currentTarget.style.backgroundColor = "#FBFAFD";
            }}
          >
            <t.icon size={20} color="#8F3C23" strokeWidth={1.6} />
            <div
              className="mt-3"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: 16,
                color: "#1B1F4B",
                lineHeight: 1.2,
              }}
            >
              {t.label}
            </div>
            <div
              style={{
                color: "#6C6B85",
                fontSize: 12,
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                marginTop: 2,
              }}
            >
              {t.sub}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ──────────────── § 03 · Project card with progress ──────────────── */

function ProjectCard({ p }) {
  const pct = stageProgress(p.current_stage);
  const blocker = stageBlocker(p.current_stage);
  return (
    <Link
      to={`/owner/projects/${p.id}`}
      data-testid={`ws-project-${p.id}`}
      style={{
        border: "1px solid #D9D8E4",
        borderLeft: `3px solid ${blocker.color}`,
        borderRadius: 4,
        background: "#FBFAFD",
        padding: "16px 18px 16px 18px",
        textDecoration: "none",
        color: "inherit",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        transition: "border-color 200ms ease, box-shadow 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderTopColor = "#B14E2F";
        e.currentTarget.style.borderRightColor = "#B14E2F";
        e.currentTarget.style.borderBottomColor = "#B14E2F";
        e.currentTarget.style.boxShadow =
          "0 1px 0 rgba(27,31,75,0.04), 0 12px 24px -10px rgba(27,31,75,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderTopColor = "#D9D8E4";
        e.currentTarget.style.borderRightColor = "#D9D8E4";
        e.currentTarget.style.borderBottomColor = "#D9D8E4";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Headline: who is blocking */}
      <div className="flex items-start justify-between gap-3">
        <WaitingOnPill
          stage={p.current_stage}
          testId={`ws-blocker-${p.id}`}
        />
        <ChevronRight size={16} color="#9AA0C0" />
      </div>

      {/* Project identity */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 19,
            color: "#1B1F4B",
            lineHeight: 1.22,
          }}
        >
          {p.design_name || p.design?.name || "Untitled project"}
        </div>
        <div
          className="mt-1"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          {p.parcel_address || p.parcel?.address || "Parcel"}
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div
          aria-hidden
          style={{
            height: 4,
            borderRadius: 999,
            background: "#ECEBF1",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: "#B14E2F",
              transition: "width 360ms ease",
            }}
          />
        </div>
        <div
          className="flex items-center justify-between mt-2"
          style={{ fontSize: 11.5 }}
        >
          <span
            className="wn-label"
            style={{ color: "#6C6B85" }}
          >
            Step {Math.max(1, Math.round((pct / 100) * STAGE_ORDER.length))} of {STAGE_ORDER.length}
          </span>
          <span
            className="wn-label"
            style={{ color: "#6C6B85" }}
          >
            {p.current_stage}
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ──────────────── § 05 · Financial snapshot ──────────────── */

function FinanceSnapshot({ finance }) {
  if (!finance) return null;
  const { lifetime_total = 0, next_charge } = finance;
  return (
    <div style={{ marginTop: 56 }} data-testid="ws-finance">
      <SectionHeading
        marker="§ 05"
        eyebrow="Spending"
        title="Financial snapshot."
        trailing={
          <Link
            to="/owner/payments"
            className="wn-cta-arrow inline-flex items-center"
            style={{
              color: "#8F3C23",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 13,
              gap: 6,
              textDecoration: "none",
            }}
          >
            View all payments <ArrowUpRight size={13} />
          </Link>
        }
      />
      <div
        className="mt-6 grid md:grid-cols-2"
        style={{ gap: 16 }}
      >
        <div
          style={{
            border: "1px solid #D9D8E4",
            borderRadius: 4,
            backgroundColor: "#FBFAFD",
            padding: "22px 24px 20px",
          }}
        >
          <div className="wn-label" style={{ color: "#6C6B85" }}>
            Lifetime spend
          </div>
          <div
            className="tabular-nums mt-1"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 800,
              fontSize: 34,
              color: "#1B1F4B",
              letterSpacing: "-0.01em",
              lineHeight: 1,
            }}
            data-testid="ws-lifetime"
          >
            {formatPriceCompact(lifetime_total)}
          </div>
          <div
            className="mt-2"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
            }}
          >
            Across consultations and plan purchases since you joined.
          </div>
        </div>
        <div
          style={{
            border: "1px solid #D9D8E4",
            borderRadius: 4,
            backgroundColor: next_charge ? "#FFF6EA" : "#FBFAFD",
            padding: "22px 24px 20px",
          }}
        >
          <div className="wn-label" style={{ color: "#8F3C23" }}>
            Next expected charge
          </div>
          {next_charge ? (
            <>
              <div
                className="tabular-nums mt-1"
                style={{
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 800,
                  fontSize: 34,
                  color: "#8F3C23",
                  letterSpacing: "-0.01em",
                  lineHeight: 1,
                }}
                data-testid="ws-next-charge"
              >
                {formatPriceCompact(next_charge.amount)}
              </div>
              <div
                className="mt-2"
                style={{ fontSize: 14, color: "#272636", lineHeight: 1.4 }}
              >
                {next_charge.label}
              </div>
              <Link
                to={next_charge.href}
                className="mt-3 inline-flex items-center wn-cta-arrow"
                style={{
                  color: "#8F3C23",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: 13,
                  gap: 6,
                  textDecoration: "none",
                }}
              >
                Pay now <ArrowUpRight size={13} />
              </Link>
            </>
          ) : (
            <div
              className="mt-2"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#272636",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              No upcoming charges. You&rsquo;ll be notified before any plan
              purchase or consultation deposit.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────── § 07 · Civic context ──────────────── */

function CivicContext({ civic }) {
  if (!civic) return null;
  const tiles = [
    {
      icon: Compass,
      value: civic.designs_in_zone,
      label: "Designs in registry",
      sub: "Pre-reviewed by city staff",
      to: "/marketplace",
    },
    {
      icon: Hammer,
      value: civic.active_builds_nearby,
      label: "Builds underway",
      sub: "Across Indianapolis right now",
      to: "/parcels",
    },
    {
      icon: AlertCircle,
      value: civic.code_changes_in_zone,
      label: "Code changes",
      sub: "Affecting permissive zoning",
      to: "/resources",
    },
  ];
  return (
    <div style={{ marginTop: 56 }} data-testid="ws-civic">
      <SectionHeading
        marker="§ 07"
        eyebrow="Your neighborhood"
        title={`${civic.neighborhood} at a glance.`}
        dek="What the platform is doing in and around the parcels you care about."
      />
      <div
        className="mt-6 grid grid-cols-1 md:grid-cols-3"
        style={{ gap: 12 }}
      >
        {tiles.map((t) => (
          <Link
            key={t.label}
            to={t.to}
            className="wn-civic-tile"
            style={{
              border: "1px solid #D9D8E4",
              borderRadius: 4,
              backgroundColor: "#FBFAFD",
              padding: "18px 20px",
              textDecoration: "none",
              color: "inherit",
              display: "block",
              transition: "border-color 160ms ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "#B14E2F")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "#D9D8E4")
            }
          >
            <div className="flex items-center gap-2">
              <t.icon size={16} color="#8F3C23" strokeWidth={1.6} />
              <span className="wn-label" style={{ color: "#6C6B85" }}>
                {t.label}
              </span>
            </div>
            <div
              className="tabular-nums mt-1"
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 800,
                fontSize: 30,
                color: "#1B1F4B",
                lineHeight: 1,
              }}
            >
              {t.value ?? "—"}
            </div>
            <div
              style={{
                color: "#6C6B85",
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                fontSize: 12.5,
                marginTop: 4,
              }}
            >
              {t.sub}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ──────────────── § 06 · Conversations & saved ──────────────── */

function ConversationsBlock({ items }) {
  return (
    <div data-testid="ws-conversations">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 22,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            § 06 · Recent conversations
          </span>
        </div>
        <Link
          to="/owner/messages"
          className="wn-cta-arrow inline-flex items-center"
          style={{
            color: "#8F3C23",
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 12.5,
            gap: 6,
            textDecoration: "none",
          }}
        >
          See all
          <ArrowUpRight size={12} />
        </Link>
      </div>
      <div
        className="mt-4"
        style={{
          borderTop: "1px solid #D9D8E4",
          borderBottom: "1px solid #D9D8E4",
        }}
      >
        {(items || []).slice(0, 3).map((c) => (
          <Link
            key={c.id}
            to={`/owner/messages?id=${c.id}`}
            className="grid items-baseline"
            style={{
              gridTemplateColumns: "auto 1fr auto",
              gap: 14,
              padding: "14px 4px",
              borderTop: "1px solid #ECEBF1",
              textDecoration: "none",
              color: "inherit",
            }}
            data-testid={`ws-conv-${c.id}`}
          >
            <span
              aria-hidden
              style={{
                width: 8, height: 8, borderRadius: 999,
                background: c.unread ? "#B14E2F" : "transparent",
                border: c.unread ? "none" : "1px solid #D9D8E4",
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 700,
                  color: "#1B1F4B",
                  fontSize: 14,
                  lineHeight: 1.25,
                }}
              >
                {c.architect_firm || "Architect"}
              </div>
              <div
                style={{
                  color: "#6C6B85",
                  fontSize: 12.5,
                  marginTop: 2,
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                }}
              >
                {c.design_name || "Consultation"}
              </div>
            </div>
            <span
              className="wn-label"
              style={{ color: "#6C6B85" }}
            >
              {relativeDate(c.last_activity_at || c.opened_at)}
            </span>
          </Link>
        ))}
        {(items || []).length === 0 && (
          <div
            style={{
              padding: "16px 4px",
              color: "#6C6B85",
              fontStyle: "italic",
              fontSize: 14,
            }}
          >
            No conversations yet.
          </div>
        )}
      </div>
    </div>
  );
}

function SavedDesignsBlock({ items }) {
  return (
    <div data-testid="ws-saved">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 22,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            § 06 · Saved designs
          </span>
        </div>
        <Link
          to="/owner/saved"
          className="wn-cta-arrow inline-flex items-center"
          style={{
            color: "#8F3C23",
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 12.5,
            gap: 6,
            textDecoration: "none",
          }}
        >
          See all <ArrowUpRight size={12} />
        </Link>
      </div>
      <div
        className="mt-4"
        style={{
          borderTop: "1px solid #D9D8E4",
          borderBottom: "1px solid #D9D8E4",
        }}
      >
        {(items || []).slice(0, 3).map((d) => (
          <Link
            key={d.id}
            to={`/marketplace/${d.id}`}
            className="grid items-center"
            style={{
              gridTemplateColumns: "56px 1fr auto",
              gap: 14,
              padding: "12px 4px",
              borderTop: "1px solid #ECEBF1",
              textDecoration: "none",
              color: "inherit",
            }}
            data-testid={`ws-saved-${d.id}`}
          >
            <span
              aria-hidden
              style={{
                width: 56, height: 42, borderRadius: 4,
                backgroundColor: "#EEEDF3",
                backgroundImage: d.hero_image_ref
                  ? `url(${assetUrl(d.hero_image_ref)})`
                  : undefined,
                backgroundSize: "cover", backgroundPosition: "center",
                border: "1px solid #D9D8E4",
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 700,
                  color: "#1B1F4B",
                  fontSize: 14,
                  lineHeight: 1.25,
                }}
              >
                {d.name}
              </div>
              <div
                style={{
                  color: "#6C6B85",
                  fontSize: 12.5,
                  marginTop: 2,
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                }}
              >
                {d.architect_firm || d.architect_name || "Architect"}
              </div>
            </div>
            <Bookmark size={14} color="#8F3C23" />
          </Link>
        ))}
        {(items || []).length === 0 && (
          <div
            style={{
              padding: "16px 4px",
              color: "#6C6B85",
              fontStyle: "italic",
              fontSize: 14,
            }}
          >
            No saved designs yet.{" "}
            <Link to="/marketplace" style={{ color: "#8F3C23" }}>
              Browse the marketplace →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────── § 08 · Activity feed ──────────────── */

function ActivityFeed({ items, projects = [] }) {
  const [expanded, setExpanded] = useState(false);
  const [projectFilter, setProjectFilter] = useState("all");
  if (!items || items.length === 0) return null;
  const DEFAULT_LIMIT = 6;

  // Only show the per-project chips when (a) we have >1 project and
  // (b) the user has expanded the feed. Keeps the collapsed state
  // calm; surfaces scannability the moment they ask for "view all".
  const filterable = expanded && projects.length > 1;
  const filtered = projectFilter === "all"
    ? items
    : items.filter((a) => a.project_id === projectFilter);
  const visible = expanded ? filtered : filtered.slice(0, DEFAULT_LIMIT);
  const hasMore = filtered.length > DEFAULT_LIMIT;

  return (
    <div style={{ marginTop: 56 }} data-testid="ws-activity">
      <SectionHeading
        marker="§ 08"
        eyebrow="Audit trail"
        title="Recent activity."
        trailing={
          (hasMore || expanded) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="ws-activity-toggle"
              className="wn-cta-arrow inline-flex items-center"
              style={{
                color: "#8F3C23",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 13,
                gap: 6,
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              {expanded
                ? "Show less"
                : `View all (${items.length})`}
              <ArrowUpRight size={13} />
            </button>
          )
        }
      />

      {filterable && (
        <div
          className="mt-5 flex flex-wrap items-center"
          style={{ gap: 8 }}
          data-testid="ws-activity-filters"
        >
          <FilterChip
            label={`All projects (${items.length})`}
            active={projectFilter === "all"}
            onClick={() => setProjectFilter("all")}
            testid="ws-activity-filter-all"
          />
          {projects.map((p) => {
            const count = items.filter(
              (a) => a.project_id === p.id,
            ).length;
            const name = p.design_name || p.design?.name || "Untitled";
            return (
              <FilterChip
                key={p.id}
                label={`${name} (${count})`}
                disabled={count === 0}
                active={projectFilter === p.id}
                onClick={() => setProjectFilter(p.id)}
                testid={`ws-activity-filter-${p.id}`}
              />
            );
          })}
        </div>
      )}

      <ul
        className="mt-6"
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          borderTop: "1px solid #D9D8E4",
          borderBottom: "1px solid #D9D8E4",
        }}
      >
        {visible.length === 0 ? (
          <li
            style={{
              padding: "20px 4px",
              color: "#6C6B85",
              fontStyle: "italic",
              fontSize: 14,
            }}
          >
            No activity for this project yet.
          </li>
        ) : (
          visible.map((a, i) => (
          <li
            key={i}
            className="grid items-center"
            style={{
              gridTemplateColumns: "auto 1fr auto",
              gap: 16,
              padding: "12px 4px",
              borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
            }}
            data-testid={`ws-activity-${a.action}`}
          >
            <ActivityIcon size={14} color="#8F3C23" strokeWidth={1.6} />
            <div style={{ minWidth: 0, fontSize: 13.5, lineHeight: 1.5 }}>
              <span style={{ color: "#1B1F4B", fontWeight: 700 }}>
                {a.actor?.name || "Platform"}
              </span>{" "}
              <span style={{ color: "#272636" }}>{actionLabel(a)}</span>
              {a.dedup_count > 1 && (
                <span
                  style={{
                    color: "#6C6B85",
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    marginLeft: 6,
                  }}
                >
                  · +{a.dedup_count - 1} similar
                </span>
              )}
              {a.record_type && a.record_id && (
                <div
                  className="tabular-nums"
                  style={{
                    color: "#6C6B85",
                    fontSize: 11.5,
                    marginTop: 1,
                    fontFamily: '"Nunito Sans", sans-serif',
                    letterSpacing: "0.02em",
                  }}
                >
                  {a.record_type}/{a.record_id}
                </div>
              )}
            </div>
            <span
              className="wn-label"
              style={{ color: "#6C6B85", whiteSpace: "nowrap" }}
            >
              {relativeDate(a.at)}
            </span>
          </li>
          ))
        )}
      </ul>
    </div>
  );
}

function FilterChip({ label, active, disabled, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: "0.04em",
        padding: "6px 12px",
        borderRadius: 999,
        border: active ? "1px solid #8F3C23" : "1px solid #D9D8E4",
        background: active ? "#8F3C23" : "transparent",
        color: active ? "#FBFAFD" : (disabled ? "#9AA0C0" : "#1B1F4B"),
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "background-color 160ms ease, border-color 160ms ease",
      }}
    >
      {label}
    </button>
  );
}

/* ──────────────── shared bits ──────────────── */

function SectionHeading({ marker, eyebrow, title, dek, trailing }) {
  return (
    <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-end">
      <div>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 22,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            {marker} · {eyebrow}
          </span>
        </div>
        {title && (
          <h2
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(24px, 2.6vw, 32px)",
              lineHeight: 1.15,
              color: "#1B1F4B",
              letterSpacing: "-0.005em",
              margin: 0,
            }}
          >
            {title}
          </h2>
        )}
        {dek && (
          <p
            className="mt-2"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 15,
              lineHeight: 1.55,
              maxWidth: 640,
              margin: 0,
            }}
          >
            {dek}
          </p>
        )}
      </div>
      {trailing && <div>{trailing}</div>}
    </div>
  );
}

function EmptyTile({ title, body, cta }) {
  return (
    <div
      className="mt-6"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        padding: "28px 28px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 20,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 20,
            color: "#1B1F4B",
            lineHeight: 1.2,
          }}
        >
          {title}
        </div>
        <div
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 14,
          }}
        >
          {body}
        </div>
      </div>
      {cta && (
        <Link
          to={cta.to}
          className="wn-btn wn-btn-accent inline-flex items-center"
          style={{ gap: 8 }}
        >
          {cta.label}
          <ArrowUpRight size={14} />
        </Link>
      )}
    </div>
  );
}
